import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { getDocument } from '@/lib/utils';

const loadImage = (url: string, useCors = true) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    if (useCors) img.crossOrigin = 'anonymous';

    img.onload = () => resolve(img);
    img.onerror = () => reject();

    // Bust browser cache for CORS requests — the same URL may have been loaded
    // without crossOrigin elsewhere, and the cached non-CORS response would
    // fail the CORS check.
    if (useCors) {
      const imageUrl = new URL(url, getDocument()?.location.href);
      imageUrl.searchParams.set('x-cors', '1');
      img.src = imageUrl.toString();
    } else {
      img.src = url;
    }
  });

/**
 * Loads the image at imageUrl when the modal is open, with CORS fallback.
 * Returns a ref holding the loaded HTMLImageElement and a loaded flag.
 */
export function useOriginalImage(
  imageUrl: string,
  isOpen: boolean,
): { originalImgRef: RefObject<HTMLImageElement | null>; originalLoaded: boolean } {
  const originalImgRef = useRef<HTMLImageElement | null>(null);
  const [originalLoaded, setOriginalLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || !imageUrl) return;

    let cancelled = false;
    setOriginalLoaded(false);

    loadImage(imageUrl)
      .catch(() => loadImage(imageUrl, false))
      .then((img) => {
        if (cancelled) return;
        originalImgRef.current = img;
        setOriginalLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        console.warn('Failed to load image', imageUrl);
      });

    return () => {
      cancelled = true;
      originalImgRef.current = null;
    };
  }, [imageUrl, isOpen]);

  return { originalImgRef, originalLoaded };
}
